import { useEffect, useRef, useState } from 'react';

import {
  faArrowLeft,
  faArrowRight,
  faExclamationTriangle,
  faPaperPlane,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
} from '@repo/videa-components/src/index';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';

import { useCreateReferralByPatientId } from '@/react-query/useCreateReferralByPatientId';
import { usePreferences } from '@/react-query/usePreferences';
import {
  useReferralDetails,
  useUpdateReferral,
  useUpdateReferralById,
} from '@/react-query/useReferralInfo';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';
import { PracticeFeatureFlagKey, useFeatureFlag } from '@/utils/user';
import { isValidEmail } from '@/utils/validation';
import type { ValidUniversalToothId } from '@videa/utils/src/normalizeToothIds';

import type { ReferralFormValues } from '../types';

import { AttachmentUploadTab } from './AttachmentUpload/AttachmentUploadTab';
import { PatientAndDoctorTab } from './PatientAndDoctor/PatientAndDoctorTab';
import { ReferralPdfDownload } from './ReferralPdfDownload/ReferralPdfDownload';
import { SimpleOdontogram } from './ReferralSummary/SimpleOdontogram';
import { TreatingSpecialistTab } from './TreatingSpecialist/TreatingSpecialistTab';
import { TreatingSpecialistTabV2 } from './TreatingSpecialist/TreatingSpecialistTabV2';
import { VisitAttachmentsTab } from './VisitAttachments/VisitAttachmentsTab';

/**
 * ReferralForm with Tab Duration Tracking
 *
 * Tab duration tracking has been implemented to track time spent on each step.
 * To verify this is working:
 * 1. Open browser dev tools and go to Network tab
 * 2. Filter for requests containing "mixpanel" or "events"
 * 3. Navigate between referral form tabs
 * 4. Look for 'user.referral.tab.duration' events being sent
 * 5. Each event will contain: step, stepName, duration, fromStep, toStep, unifiedAppointmentId
 */
interface ReferralFormProps {
  patientId: string;
  patientVideaId?: string; // Optional videaId for patient-based referrals
  isLoading?: boolean;
  onCancel: () => void;
  defaultValues?: Partial<ReferralFormValues>;
  appointment?: any;
  unifiedAppointmentId?: string;
  onClose: () => void;
  videaPracticeId: string;
  mode?: 'appointment' | 'patient';
  pmsPatientVideaId?: string;
  imagingPatientVideaId?: string;
  hideDownloadButton?: boolean;
  referralId?: string;
  onSubmitSuccess: () => void;
}

interface ReferralSubmissionError {
  statusCode: 400;
  message: string;
  error: 'Bad Request';
}

// Step field definitions
const STEP_FIELDS = {
  1: ['specialtyId', 'referralPracticeId'],
  2: [
    'patientFirstName',
    'patientLastName',
    'patientDOBDay',
    'patientDOBMonth',
    'patientDOBYear',
    'patientPhone',
    'doctorFirstName',
    'doctorLastName',
    'practiceEmail',
    'doctorPhone',
  ],
  3: ['selectedTreatments'],
  4: ['selectedImagePaths', 'selectedImageAnalysisIds'], // Require at least one image with analysis ID
  5: [], // Step 5 is the attachment upload step (optional)
  6: [], // Step 6 is the PDF download step
};

// Simple validation function
const validateStep = (values: ReferralFormValues, step: number): boolean => {
  const fields = STEP_FIELDS[step as keyof typeof STEP_FIELDS];

  for (const field of fields) {
    const value = values[field as keyof ReferralFormValues];

    if (field === 'practiceEmail' && value && typeof value === 'string') {
      if (!isValidEmail(value)) {
        return false;
      }
    } else if (field === 'selectedTreatments') {
      if (!Array.isArray(value) || value.length === 0) {
        return false;
      }
    } else if (field === 'selectedImagePaths') {
      if (!Array.isArray(value) || value.length === 0) {
        return false;
      }
    } else if (field === 'selectedImageAnalysisIds') {
      if (!Array.isArray(value) || value.length === 0) {
        return false;
      }
      // Ensure all analysis IDs are valid strings
      if (!value.every((id) => typeof id === 'string' && id.trim() !== '')) {
        return false;
      }
    } else if (!value) {
      return false;
    }
  }

  // Step 1 specific validation for specialist selection
  if (step === 1) {
    if (values.useNextAvailableSpecialist) {
      if (!values.specialtyId || !values.referralPracticeId) {
        return false;
      }
    } else if (
      !values.specialtyId ||
      !values.referralPracticeId ||
      !values.referralSpecialistId
    ) {
      return false;
    }
  }

  // Additional validation for optional patientEmail (if provided)
  if (
    step === 2 &&
    values.patientEmail &&
    typeof values.patientEmail === 'string'
  ) {
    if (!isValidEmail(values.patientEmail)) {
      return false;
    }
  }

  return true;
};

export const ReferralForm = ({
  onCancel,
  isLoading = false,
  patientId,
  patientVideaId,
  defaultValues,
  appointment,
  unifiedAppointmentId,
  onClose,
  videaPracticeId,
  mode = 'appointment',
  pmsPatientVideaId,
  imagingPatientVideaId,
  hideDownloadButton = false,
  referralId,
  onSubmitSuccess,
}: ReferralFormProps) => {
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([1])); // Track visited steps
  const [isReferralSubmitted, setIsReferralSubmitted] = useState(false); // Track if referral has been submitted locally in form
  const [submissionError, setSubmissionError] =
    useState<ReferralSubmissionError | null>(null); // Track submission errors
  const [isUploadingFiles, setIsUploadingFiles] = useState(false); // Track file upload state

  // Tab duration tracking
  const tabStartTime = useRef<Date>(new Date());
  const previousStep = useRef<number>(1);

  const { t } = useLingui();
  const [selectedImages, setSelectedImages] = useState<
    Array<{ path: string; analysisId: string }>
  >([]);
  const [selectedPractice, setSelectedPractice] = useState<
    | {
        practice_id: number;
        practice_name: string;
        referral_practice_address?: string;
        referral_practice_phone?: string;
      }
    | undefined
  >(undefined);
  const queryClient = useQueryClient();
  const { trackEvent } = useTrackEvent();

  const methods = useForm<ReferralFormValues>({
    mode: 'onChange',
    defaultValues: { ...defaultValues },
  });

  const {
    formState: { errors },
    handleSubmit,
    trigger,
    setValue,
    watch,
  } = methods;

  // Use different mutation based on mode
  const { mutate: updateReferral } = useUpdateReferral(
    unifiedAppointmentId || '',
  );

  const { mutate: updateReferralById } = useUpdateReferralById();
  const { mutate: createReferralByPatientId } = useCreateReferralByPatientId();
  // Get referral details to get the actual referral ID
  const { data: referralDetails } = useReferralDetails(
    appointment?.appointment?.unifiedId || '',
    {
      enabled: !!appointment?.appointment?.unifiedId,
    },
  );

  const { data: preferences } = usePreferences();

  const shouldSkipAttachmentsStep =
    preferences?.oralinxReferralRequestVersion !== 2;

  // Skip step 4 (Visit & Image Attachment) if no patient ID is available
  // The usePatientVisits hook can work with just patientId (V1 endpoint) or videaId (V2 endpoint)
  const shouldSkipVisitImagesStep = !patientId;

  // Update form values when selected images change
  useEffect(() => {
    // Filter out images without valid analysis IDs
    const validImages = selectedImages.filter(
      (img) => img.path && img.analysisId && img.analysisId.trim() !== '',
    );

    setValue(
      'selectedImagePaths',
      validImages.map((img) => img.path),
    );
    setValue(
      'selectedImageAnalysisIds',
      validImages.map((img) => img.analysisId),
    );

    // Log for debugging
    if (selectedImages.length !== validImages.length) {
      console.warn(
        'Some selected images were filtered out due to missing analysis IDs:',
        {
          totalSelected: selectedImages.length,
          validImages: validImages.length,
          invalidImages: selectedImages.filter(
            (img) => !img.analysisId || img.analysisId.trim() === '',
          ),
        },
      );
    }
  }, [selectedImages, setValue]);

  // Watch form values for step completion
  const formValues = watch();

  // Check if current step is complete
  const isStepComplete = (step: number): boolean => {
    // Only consider steps complete if they've been visited
    if (!visitedSteps.has(step)) {
      return false;
    }

    // Special handling for step 6 (PDF download) - only complete if referral was submitted
    if (step === 6) {
      return isReferralSubmitted;
    }

    // Step 4 (Visit & Image Attachment) should be skipped if no imaging patient ID
    if (step === 4 && shouldSkipVisitImagesStep) {
      return true; // Skip this step entirely
    }

    // Step 4 (Visit & Image Attachment) is optional when oralinxReferralRequestVersion is 2
    if (step === 4 && preferences?.oralinxReferralRequestVersion === 2) {
      const hasImages =
        formValues.selectedImagePaths &&
        formValues.selectedImagePaths.length > 0;
      const hasAnalysisIds =
        formValues.selectedImageAnalysisIds &&
        formValues.selectedImageAnalysisIds.length > 0;

      // If no images selected, step is optional and considered complete
      if (!hasImages && !hasAnalysisIds) {
        return true;
      }

      // If images are selected, validate them normally
      return validateStep(formValues, step);
    }

    // Skip step 5 validation if attachments should be skipped
    if (step === 5 && shouldSkipAttachmentsStep) {
      return true; // Consider it complete if we're skipping it
    }

    return validateStep(formValues, step);
  };

  // Check if navigation to a specific step should be allowed
  // Navigation forward requires all previous steps to be complete
  const canNavigateToStep = (stepNumber: number): boolean => {
    // Always allow navigating backwards
    if (stepNumber <= activeStep) {
      return true;
    }

    // For forward navigation, check that all previous steps are complete
    // Build list of steps that need to be checked (excluding skipped steps)
    const stepsToCheck = [1, 2, 3];
    if (!shouldSkipVisitImagesStep) {
      stepsToCheck.push(4);
    }
    if (!shouldSkipAttachmentsStep) {
      stepsToCheck.push(5);
    }

    // Check all steps before the target step
    for (const step of stepsToCheck) {
      if (step >= stepNumber) break;

      // Step 4 is optional when oralinxReferralRequestVersion is 2
      if (step === 4 && preferences?.oralinxReferralRequestVersion === 2) {
        continue; // Skip validation for optional step 4
      }

      // Step 5 is always optional
      if (step === 5) {
        continue; // Skip validation for optional step 5
      }

      if (!validateStep(formValues, step)) {
        return false;
      }
    }

    return true;
  };

  const navigateToStep = (stepNumber: number) => {
    // Prevent navigation to any step except step 6 after referral has been submitted
    if (isReferralSubmitted && stepNumber < 6) {
      return;
    }

    // Prevent navigation to step 4 if no imaging patient ID is available
    if (stepNumber === 4 && shouldSkipVisitImagesStep) {
      return;
    }

    // Prevent navigation to step 5 if attachments should be skipped
    if (stepNumber === 5 && shouldSkipAttachmentsStep) {
      return;
    }

    // Prevent forward navigation if previous steps are not complete
    if (!canNavigateToStep(stepNumber)) {
      return;
    }

    setActiveStep(stepNumber);
    setVisitedSteps((prev) => new Set([...prev, stepNumber]));

    // Clear submission error when navigating to step 4 to add images
    if (stepNumber === 4) {
      setSubmissionError(null);
    }
  };

  const handleNextClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (activeStep < 5) {
      const fields = STEP_FIELDS[activeStep as keyof typeof STEP_FIELDS];
      const isValid = await trigger(fields as any);

      if (isValid) {
        // Skip step 4 (visit images) if no imaging patient ID is available
        if (activeStep === 3 && shouldSkipVisitImagesStep) {
          // Also skip step 5 if attachments should be skipped
          if (shouldSkipAttachmentsStep) {
            navigateToStep(6); // Go directly to PDF download
          } else {
            navigateToStep(5); // Go to attachments
          }
        }
        // Skip step 5 (attachments) if preference is not set
        else if (activeStep === 4 && shouldSkipAttachmentsStep) {
          navigateToStep(6); // Go directly to PDF download
        } else {
          navigateToStep(activeStep + 1);
        }
      }
    }
  };

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // Prevent navigation back after referral has been submitted
    if (isReferralSubmitted) {
      return;
    }

    if (activeStep > 1) {
      let targetStep = activeStep - 1;

      // Skip step 4 if it should be skipped
      if (targetStep === 4 && shouldSkipVisitImagesStep) {
        targetStep = 3;
      }

      navigateToStep(targetStep);
    }
  };

  const handleFormSubmit = async (data: ReferralFormValues) => {
    // Prevent multiple submissions
    if (isReferralSubmitted) {
      return;
    }

    const isValid = await trigger();
    if (!isValid) return;

    // Clear any previous submission errors
    setSubmissionError(null);

    // Set local loading state
    setIsSubmittingLocal(true);

    // Optimistically notify parent that submission started
    onSubmitSuccess();

    const toothNumbers = (data.selectedTeeth || []).map(
      (toothId: ValidUniversalToothId) => toothId.toString(),
    );

    // Format patient date of birth from individual fields
    const patientDateOfBirth =
      data.patientDOBDay && data.patientDOBMonth && data.patientDOBYear
        ? `${data.patientDOBYear}-${data.patientDOBMonth}-${data.patientDOBDay}`
        : undefined;

    // Ensure we have both image URLs and analysis IDs
    const imageUrls = data.selectedImagePaths || [];
    const analysisIds = data.selectedImageAnalysisIds || [];

    // Validate that we have analysis IDs for all selected images
    if (imageUrls.length > 0 && analysisIds.length === 0) {
      console.warn(
        'Referral submission: No analysis IDs found for selected images',
        {
          imageUrls,
          selectedImages,
          formData: data,
        },
      );
    }

    // Validate that the number of image URLs matches the number of analysis IDs
    if (imageUrls.length !== analysisIds.length) {
      console.error(
        'Referral submission: Mismatch between image URLs and analysis IDs',
        {
          imageUrlsCount: imageUrls.length,
          analysisIdsCount: analysisIds.length,
          imageUrls,
          analysisIds,
        },
      );

      // Set error and prevent submission
      setSubmissionError({
        statusCode: 400,
        message:
          'Invalid image data: Image URLs and analysis IDs do not match. Please try selecting images again.',
        error: 'Bad Request',
      });
      setIsSubmittingLocal(false);
      return;
    }

    trackEvent(mixpanelEvents.referralCompleted, {
      unifiedAppointmentId: unifiedAppointmentId || 'patient-based-referral',
    });

    const referralPayload = {
      patientFirstName: data.patientFirstName,
      patientLastName: data.patientLastName,
      patientEmail: data.patientEmail,
      patientPhone: data.patientPhone,
      patientDateOfBirth: patientDateOfBirth,
      referralReason: data.finalNotes || data.narrative || '',
      toothNumbers,
      status: 'submitted' as const,
      imageUrls,
      analysisIds,
      specialtyId: data.specialtyId,
      allTeeth: data.selectionMode === 'selectAll',
      proceduresIds: data.selectedTreatments || [],
      referralPracticeId: data.referralPracticeId,
      referralSpecialistId: data.useNextAvailableSpecialist
        ? undefined
        : data.referralSpecialistId,
      useNextAvailableSpecialist: data.useNextAvailableSpecialist || false,
      doctorFirstName: data.doctorFirstName,
      doctorLastName: data.doctorLastName,
      doctorPhone: data.doctorPhone,
      practiceEmail: data.practiceEmail,
      narrative: data.narrative || '',
    };

    // Define success/error handlers
    const handleSuccess = () => {
      // Clear any submission errors
      setSubmissionError(null);
      // Mark referral as submitted locally and navigate to PDF download step
      setIsReferralSubmitted(true);
      setIsSubmittingLocal(false);
      navigateToStep(6);
      // Invalidate referrals queries after successful submission
      queryClient.invalidateQueries(['referrals']);
    };

    const handleError = (error: any) => {
      // Parse error response
      const errorData = error.response?.data;
      if (errorData && errorData.statusCode === 400) {
        setSubmissionError(errorData);
      } else {
        // Fallback for other errors
        setSubmissionError({
          statusCode: 400,
          message:
            'Failed to submit referral. Please try again or contact support.',
          error: 'Bad Request',
        });
      }

      // Navigate to step 4 to show the error (step 4 is where submission errors are displayed)
      // This ensures the error is visible even if step 5 was skipped
      navigateToStep(4);

      // Reset loading state
      setIsSubmittingLocal(false);
    };

    if (referralId) {
      updateReferralById(
        {
          referralId,
          data: referralPayload,
        },
        {
          onSuccess: handleSuccess,
          onError: handleError,
        },
      );
    } else if (mode === 'patient') {
      // Patient-based referral using POST /referrals/v2
      createReferralByPatientId(
        {
          ...referralPayload,
          practiceId: videaPracticeId,
          ...(pmsPatientVideaId && { pmsPatientVideaId }),
          ...(imagingPatientVideaId && { imagingPatientVideaId }),
        },
        {
          onSuccess: handleSuccess,
          onError: handleError,
        },
      );
    } else {
      // Appointment-based referral using PUT /referrals/:id
      // Optimistically update the referral status cache
      if (unifiedAppointmentId) {
        queryClient.setQueryData(['referral', unifiedAppointmentId, 'status'], {
          status: 'submitted',
        });
      }

      updateReferral(referralPayload, {
        onSuccess: handleSuccess,
        onError: handleError,
      });
    }
  };

  const handleCancel = () => {
    trackEvent(mixpanelEvents.referralCancelled, {
      unifiedAppointmentId: unifiedAppointmentId || 'patient-based-referral',
      currentStep: activeStep,
    });

    // Parent (ReferralModal) will handle deletion
    onCancel();
  };

  const renderStepContent = () => {
    // Show loading animation if submitting
    if (isSubmittingLocal) {
      return (
        <div className="vai-flex vai-flex-col vai-items-center vai-justify-center vai-min-h-[400px] vai-text-center vai-p-8">
          <FontAwesomeIcon
            icon={faSpinner}
            className="vai-h-12 vai-w-12 vai-text-blue-500 vai-animate-spin vai-mb-6"
          />
          <h2 className="vai-text-xl vai-font-semibold vai-text-gray-800 vai-mb-2">
            <Trans>Submitting Referral...</Trans>
          </h2>
          <p className="vai-text-gray-600 vai-max-w-md">
            <Trans>
              Please wait while we send your referral. This may take a few
              moments.
            </Trans>
          </p>
        </div>
      );
    }

    switch (activeStep) {
      case 1:
        return preferences?.enableSpecialistSearchRedesign ? (
          <TreatingSpecialistTabV2
            onPracticeSelect={setSelectedPractice}
            videaPracticeId={videaPracticeId}
          />
        ) : (
          <TreatingSpecialistTab
            onPracticeSelect={setSelectedPractice}
            videaPracticeId={videaPracticeId}
          />
        );
      case 2:
        return <PatientAndDoctorTab referralId={referralId} />;
      case 3:
        return <SimpleOdontogram />;
      case 4:
        return (
          <VisitAttachmentsTab
            patientId={patientId}
            videaId={patientVideaId}
            onImagesChange={setSelectedImages}
            isOralinxV2={preferences?.oralinxReferralRequestVersion === 2}
          />
        );
      case 5:
        // Step 5 should never be reached if attachments should be skipped
        // This is enforced by the navigateToStep guard
        if (shouldSkipAttachmentsStep) {
          console.error(
            'Step 5 should not be reachable when shouldSkipAttachmentsStep is true',
          );
          return null;
        }
        return (
          <AttachmentUploadTab
            referralId={referralId || referralDetails?.id || ''}
            onUploadStateChange={setIsUploadingFiles}
          />
        );
      case 6:
        return (
          <ReferralPdfDownload
            formData={formValues}
            appointment={appointment}
            unifiedAppointmentId={appointment?.appointment?.unifiedId}
            isDownloadStep={true}
            selectedPractice={selectedPractice}
            hideDownloadButton={hideDownloadButton}
          />
        );
      default:
        return null;
    }
  };

  const getStepLabel = (step: number) => {
    const labels = {
      1: 'Treating Specialist',
      2: 'Patient and Referring Doctor',
      3: 'Odontogram',
      4: 'Visit & Image Attachment',
      5: 'Additional Attachments',
      6: 'Referral PDF',
    };
    return labels[step as keyof typeof labels] || '';
  };

  const getStepLabelWithOptional = (step: number) => {
    const baseLabel = getStepLabel(step);
    // Step 4 is optional when oralinxReferralRequestVersion is 2
    if (step === 4 && preferences?.oralinxReferralRequestVersion === 2) {
      return (
        <>
          {baseLabel} <span className="vai-text-zinc-400">(Optional)</span>
        </>
      );
    }
    if (step === 5) {
      return (
        <>
          {baseLabel} <span className="vai-text-zinc-400">(Optional)</span>
        </>
      );
    }
    return baseLabel;
  };

  // Track tab duration when activeStep changes
  useEffect(() => {
    if (previousStep.current !== activeStep) {
      const duration = tabStartTime.current
        ? (new Date().getTime() - tabStartTime.current.getTime()) / 1000
        : 0;

      // Only track duration if we're switching from a valid step and duration is reasonable
      if (
        previousStep.current >= 1 &&
        previousStep.current <= 5 &&
        duration > 0 &&
        duration < 3600
      ) {
        const stepName = getStepLabel(previousStep.current);

        trackEvent(mixpanelEvents.referralTabDuration, {
          unifiedAppointmentId:
            unifiedAppointmentId || 'patient-based-referral',
          step: previousStep.current,
          stepName,
          duration,
          fromStep: previousStep.current,
          toStep: activeStep,
        });
      }

      // Update tracking for new step
      tabStartTime.current = new Date();
      previousStep.current = activeStep;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]); // Only depend on activeStep to prevent infinite loops

  // Track final tab duration when component unmounts
  useEffect(() => {
    const unifiedId = unifiedAppointmentId || 'patient-based-referral';
    return () => {
      const duration = tabStartTime.current
        ? (new Date().getTime() - tabStartTime.current.getTime()) / 1000
        : 0;

      if (
        previousStep.current >= 1 &&
        previousStep.current <= 5 &&
        duration > 0 &&
        duration < 3600
      ) {
        const stepName = getStepLabel(previousStep.current);

        trackEvent(mixpanelEvents.referralTabDuration, {
          unifiedAppointmentId: unifiedId,
          step: previousStep.current,
          stepName,
          duration,
          fromStep: previousStep.current,
          toStep: 'exit',
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array for unmount tracking to prevent re-creating cleanup function

  const getStepErrorMessage = () => {
    // Don't show error for step 4 if it's optional (oralinx v2)
    if (activeStep === 4 && preferences?.oralinxReferralRequestVersion === 2) {
      return null;
    }

    const fields = STEP_FIELDS[activeStep as keyof typeof STEP_FIELDS];
    const hasErrors = fields.some(
      (field) => errors[field as keyof typeof errors],
    );

    if (!hasErrors) return null;

    const messages = {
      1: t`Please complete all required fields in the Treating Specialist step.`,
      2: t`Please complete all required fields in the Patient and Referring Doctor step.`,
      3: t`Please select at least one tooth and one treatment in the Odontogram step.`,
      4: t`Please select at least one image with valid analysis data to attach to the referral.`,
    };

    const message = messages[activeStep as keyof typeof messages];
    if (!message) return null;

    return (
      <div className="vai-bg-red-50 vai-border vai-border-red-200 vai-rounded vai-p-2 vai-text-red-600 vai-text-sm">
        {message}
      </div>
    );
  };

  const getSubmissionErrorMessage = () => {
    if (!submissionError) return null;

    // Clear error when user navigates away from step 4
    if (activeStep !== 4) {
      setSubmissionError(null);
      return null;
    }

    const isNoAttachmentsError = submissionError.message.includes(
      'without attachments',
    );
    const isNoValidImagesError = submissionError.message.includes(
      'without valid image attachments',
    );

    return (
      <Alert variant="destructive" className="vai-mb-4">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="vai-h-4 vai-w-4 vai-text-red-600"
        />
        <AlertTitle className="vai-text-red-800 vai-font-semibold">
          <Trans>Cannot Submit Referral</Trans>
        </AlertTitle>
        <AlertDescription className="vai-text-red-700 vai-mt-2">
          <div className="vai-space-y-3">
            <p className="vai-font-medium">
              <Trans>Missing Required Attachments</Trans>
            </p>
            <p>
              {isNoAttachmentsError || isNoValidImagesError ? (
                <Trans>
                  This referral requires at least one image attachment before it
                  can be submitted.
                </Trans>
              ) : (
                submissionError.message
              )}
            </p>
          </div>
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-3"
        noValidate
      >
        {getStepErrorMessage()}
        {getSubmissionErrorMessage()}

        {/* Step Progress Indicator */}
        <div className="vai-mb-6">
          <div className="vai-flex vai-justify-between vai-items-center vai-bg-gray-50 vai-p-3 vai-rounded-md vai-shadow-sm">
            <h2 className="vai-font-semibold vai-text-gray-800">
              {activeStep === 6 ? (
                <Trans>Referral PDF</Trans>
              ) : (
                (() => {
                  let totalSteps = 5;
                  let displayStep = activeStep;

                  // Adjust displayed step number for skipped steps
                  if (shouldSkipVisitImagesStep && activeStep > 4) {
                    displayStep--;
                  }

                  if (shouldSkipVisitImagesStep) totalSteps--;
                  if (shouldSkipAttachmentsStep) totalSteps--;

                  const stepLabel = getStepLabelWithOptional(activeStep);
                  return (
                    <Trans>
                      Step {displayStep} of {totalSteps}: {stepLabel}
                    </Trans>
                  );
                })()
              )}
            </h2>
            <div className="vai-flex vai-space-x-1">
              {(
                [
                  1,
                  2,
                  3,
                  !shouldSkipVisitImagesStep && 4,
                  !shouldSkipAttachmentsStep && 5,
                ].filter(Boolean) as number[]
              ).map((step) => {
                // Dots are filled (blue) for current step and all previous steps
                const isFilled = step <= activeStep;
                const isAccessible = canNavigateToStep(step);
                const isDisabled =
                  (isReferralSubmitted && step < 6) || !isAccessible;

                return (
                  <button
                    key={step}
                    type="button"
                    onClick={() => navigateToStep(step)}
                    disabled={isDisabled}
                    className={`vai-w-2 vai-h-2 vai-rounded-full vai-transition-colors ${
                      isFilled ? 'vai-bg-primary' : 'vai-bg-gray-300'
                    } ${isDisabled ? 'vai-opacity-50 vai-cursor-not-allowed' : 'vai-cursor-pointer'}`}
                    title={`Step ${step}: ${getStepLabelWithOptional(step)}`}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="vai-min-h-[300px]">
          {isLoading ? (
            <div className="vai-flex vai-items-center vai-justify-center vai-h-full">
              <div className="vai-text-gray-500">
                <Trans>Loading form data...</Trans>
              </div>
            </div>
          ) : (
            renderStepContent()
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="vai-pt-4 vai-flex vai-justify-between">
          <div className="vai-flex vai-space-x-2">
            {activeStep > 1 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleBackClick}
                className="vai-h-9"
                disabled={isLoading || isReferralSubmitted}
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  className="vai-mr-1 vai-h-4 vai-w-4"
                />
                <Trans>Back</Trans>
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="vai-h-9"
                disabled={isLoading}
              >
                <Trans>Cancel</Trans>
              </Button>
            )}
          </div>

          <div>
            {activeStep < 5 &&
            !(activeStep === 4 && shouldSkipAttachmentsStep) &&
            !(
              activeStep === 3 &&
              shouldSkipVisitImagesStep &&
              shouldSkipAttachmentsStep
            ) ? (
              <Button
                type="button"
                size="sm"
                onClick={handleNextClick}
                className="vai-h-9"
                disabled={isLoading || !isStepComplete(activeStep)}
              >
                <Trans>Next</Trans>
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="vai-ml-1 vai-h-4 vai-w-4"
                />
              </Button>
            ) : (activeStep === 3 &&
                shouldSkipVisitImagesStep &&
                shouldSkipAttachmentsStep) ||
              (activeStep === 4 && shouldSkipAttachmentsStep) ||
              (activeStep === 5 && !shouldSkipAttachmentsStep) ? (
              <Button
                type="submit"
                size="sm"
                disabled={
                  isLoading ||
                  isSubmittingLocal ||
                  isReferralSubmitted ||
                  isUploadingFiles ||
                  // Only check step 4 if not skipping it
                  (!shouldSkipVisitImagesStep && !isStepComplete(4))
                }
                className="vai-h-9 vai-bg-green-600 hover:vai-bg-green-700"
              >
                {isSubmittingLocal ? (
                  <div className="vai-flex vai-items-center">
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="vai-mr-2 vai-h-4 vai-w-4 vai-animate-spin"
                    />
                    <Trans>Submitting...</Trans>
                  </div>
                ) : (
                  <>
                    <Trans>Send Referral</Trans>
                    <FontAwesomeIcon
                      icon={faPaperPlane}
                      className="vai-ml-1 vai-h-4 vai-w-4"
                    />
                  </>
                )}
              </Button>
            ) : (
              <div className="vai-flex vai-space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                  className="vai-h-9"
                >
                  <Trans>Close</Trans>
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
